/**
 * @file ContractPricesModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-11-03
 * @description "Contract these prices" (Cam, 28 Oct 2026 — N4: "Add the option
 *              to right click/ three dots menus (including when multiple
 *              selected) in pricing and costing to 'Contract these prices' and
 *              handle as we will do the contract generation from rep xlsx
 *              import"). The lines chosen on Pricing & Costing — one line, a
 *              product group, or every ticked line — become one row per
 *              customer and product, each at the sale price the screen shows
 *              (confirmed, else chosen, else proposed — said per line), and
 *              each row is made a contract exactly as the rep-reply import's
 *              Part C makes one: the SAME panel (ContractFromPricesPanel) —
 *              New contract (a period of its category or From / To dates
 *              covering every delivery, this customer or its pricing group,
 *              quantity blank unless typed), several products of one customer
 *              and category as ONE new contract ("Add another row's product to
 *              this contract…"), or Add to an existing contract with Keep /
 *              Put on hold / Archive / Replace for its products — and the same
 *              server writer (POST /api/pricing-costing/contract-prices →
 *              repReplyContractService, audited CONTRACT_FROM_PRICES). The
 *              lines it covers are linked to the contract they now take. Lines
 *              that cannot be a contract price (no sale price, invoiced, milk
 *              powder, straights, additives, not an order line) are listed and
 *              left out with why. A rep's request for a contract on the lines
 *              is shown, and settled when the contract is made.
 *
 *   1.1.0 (Cam, 3 Nov 2026 — Reports › Contracts Review): the same window for
 *   INVOICED sales — `kind="invoice"` with the report's own endpoints
 *   (`readUrl`, `writeUrl` — /api/reports/contracts-review/contract-prices…):
 *   the lines are invoice lines, each at the price it was invoiced at (put per
 *   the item's unit where the line was in another), a new contract is for the
 *   period chosen (today's by default — the dates sold need not be covered)
 *   and nothing is linked. Pricing & Costing's use is unchanged.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import Dropdown from './Dropdown';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import ContractFromPricesPanel, {
  offerOn, sharedMembers, contractProblem, contractsPayload, contractSummary, joinContract, openedOffer
} from './ContractFromPricesPanel';
import { API_URL } from '../config';
import { formatDate } from '../utils/dateFormatters';
import { money, repContractRequestText, ukDate } from '../utils/pricingCosting';
import './PricingCosting.css';

/** The rows as the window shows them: each at the price chosen for it. */
export function pricedRows(rows, priceOf = {}) {
  return (rows || []).map((r, i) => (priceOf[i] !== undefined ? { ...r, price: priceOf[i] } : r));
}

/** The first state of every row: on, on New contract, its default period. */
export function startingOffers(rows) {
  const out = {};
  (rows || []).forEach((r, i) => { const o = openedOffer(r); if (o) out[i] = o; });
  return out;
}

/**
 * What goes to the server: the contracts of rows switched on whose contract
 * is right (a row needing attention is left out — said before it is sent),
 * each product saying where its price came from.
 */
export function payloadOf({ rows, offers, shared }) {
  const idsOf = (i) => (offerOn(rows[i], offers[i]) && !contractProblem(i, rows, offers, shared) ? rows[i].lineIds : []);
  const fromOf = (i) => {
    const r = rows[i];
    const p = (r.prices || []).find(x => Math.abs(Number(x.value) - Number(r.price)) < 0.00005);
    return p && p.from.length ? { from: p.from.join(' / ') } : {};
  };
  return {
    contracts: contractsPayload({ rows, offers, shared, idsOf, extra: fromOf }),
    summary: contractSummary({ rows, offers, shared, idsOf })
  };
}

/** Rows switched on whose contract is not right yet: index → why. */
export function problemsOf({ rows, offers, shared }) {
  const out = {};
  rows.forEach((r, i) => { const p = contractProblem(i, rows, offers, shared); if (p) out[i] = p; });
  return out;
}

const dayRange = (lines) => {
  const days = lines.map(l => l.delivery_date).filter(Boolean).sort();
  if (!days.length) return '';
  return days[0] === days[days.length - 1] ? formatDate(days[0]) : `${ukDate(days[0])} – ${ukDate(days[days.length - 1])}`;
};

/**
 * @param {Array<{ id, price, from }>} props.lines   order lines with the sale price on screen
 * @param {Array<{ key, label, why }>} [props.notLines]  rows chosen that are no order line (left out)
 * @param {Function} props.onClose
 * @param {Function} props.onDone   (message) — after contracts were written
 */
const PC_READ = '/api/pricing-costing/contract-prices/read';
const PC_WRITE = '/api/pricing-costing/contract-prices';

function ContractPricesModal({
  lines, notLines = [], onClose, onDone, readUrl = PC_READ, writeUrl = PC_WRITE, kind = 'order',
  title = 'Contract these prices'
}) {
  const [read, setRead] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offers, setOffers] = useState({});
  const [shared, setShared] = useState({});
  const [priceOf, setPriceOf] = useState({});
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!lines.length) { setRead({ rows: [], left_out: [], can_contract: true }); return; }
      try {
        const res = await fetch(`${API_URL}${readUrl}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ lines })
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error || 'The lines could not be read');
        if (!live) return;
        setRead(body);
        setOffers(startingOffers(body.rows));
      } catch (err) {
        if (live) setError(err.message);
      }
    })();
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => pricedRows(read?.rows || [], priceOf), [read, priceOf]);
  const problems = useMemo(() => problemsOf({ rows, offers, shared }), [rows, offers, shared]);
  const { contracts, summary } = useMemo(() => payloadOf({ rows, offers, shared }), [rows, offers, shared]);
  const onCount = rows.filter((r, i) => offerOn(r, offers[i])).length;
  const needCount = Object.keys(problems).length;

  const save = async () => {
    setAsking(false);
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}${writeUrl}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ contracts })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'No contract was written');
      setResult(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const start = () => (needCount ? setAsking(true) : save());
  const finish = () => {
    const made = (result?.contracts || []).filter(c => c.ok).length;
    if (made) {
      onDone(kind === 'invoice'
        ? `${made} contract${made === 1 ? '' : 's'} written from the prices invoiced`
        : `${made} contract${made === 1 ? '' : 's'} written from these prices — the lines now take ${made === 1 ? 'it' : 'them'}`);
    }
    else onClose();
  };

  const n = contracts.length;
  const footer = result ? (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={finish}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
    </div>
  ) : (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={start}
          disabled={busy || !n || !read?.can_contract}
          title={!read?.can_contract ? (read?.contract_block || '') : !n ? 'Nothing ready — switch a row on and finish its contract' : `Write ${n} contract${n === 1 ? '' : 's'}${needCount ? ` — ${needCount} needing attention left out` : ''}`}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">contract_edit</span></div>
          <span className="btn-label">{busy ? 'Writing…' : `Write ${n} contract${n === 1 ? '' : 's'}`}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={result ? finish : onClose} title={title} size="xl" className="rep-replies contract-prices" footer={footer}>
      {result ? <ResultBody result={result} kind={kind} /> : (
        <PricesBody kind={kind} read={read} rows={rows} error={error} notLines={notLines} offers={offers} setOffers={setOffers}
          shared={shared} setShared={setShared} setPriceOf={setPriceOf} problems={problems}
          summary={summary} onCount={onCount} n={n} busy={busy} />
      )}
      <ConfirmDialog
        isOpen={asking}
        title="Some contracts need attention"
        type="warning"
        message={`${needCount} contract${needCount === 1 ? ' needs' : 's need'} attention and will be left out — nothing is written for ${needCount === 1 ? 'it' : 'them'}. Write the other ${n}?`}
        confirmText={`Write ${n}`}
        confirmIcon="contract_edit"
        cancelText="Go back"
        onConfirm={save}
        onCancel={() => setAsking(false)} />
    </Modal>
  );
}

/** After the write: each contract, made or refused with why. */
function ResultBody({ result, kind = 'order' }) {
  useSlideOutButton([result]);
  const list = result.contracts || [];
  return (
    <div className="rr-result">
      {list.map((c, i) => (
        <div key={i} className={`pc-flag ${c.ok ? 'info' : 'err'}`}>
          <span className="material-symbols-rounded">{c.ok ? 'check_circle' : 'error'}</span>
          <span>
            {c.ok
              ? `${c.message}${c.contract_number ? ` (${c.contract_number})` : ` (#${c.contract_id})`} — ${(c.products || []).map(p => `${p.item_code} at ${money(p.price)}`).join('; ')}${kind === 'invoice' ? ' — from the prices invoiced; the invoices are unchanged' : `; ${c.relinked} line${c.relinked === 1 ? '' : 's'} linked`}${c.settled_requests ? `; ${c.settled_requests} rep request${c.settled_requests === 1 ? '' : 's'} answered` : ''}.`
              : `Not written — ${c.message}${(c.lines || []).length ? ` (${c.lines.map(l => l.order_number).filter(Boolean).join(', ')})` : ''}`}
            {(c.warnings || []).map(w => <span key={w} className="pc-muted cp-warning"> {w}.</span>)}
          </span>
        </div>
      ))}
    </div>
  );
}

/** The window's body; mounts with the footer, so it measures the slide-out buttons. */
function PricesBody({ kind = 'order', read, rows, error, notLines, offers, setOffers, shared, setShared, setPriceOf, problems, summary, onCount, n, busy }) {
  useSlideOutButton([read, n, busy, onCount]);
  const members = sharedMembers(rows, offers);
  const setOffer = (i, patch) => setOffers(prev => ({ ...prev, [i]: { mode: 'new', scope: 'parent', ...(prev[i] || {}), ...patch } }));
  const setSharedOf = (label, patch) => setShared(prev => ({ ...prev, [label]: { ...(prev[label] || {}), ...patch } }));
  const join = (i, j) => {
    const next = joinContract({ offers, shared }, i, j);
    setOffers(next.offers);
    setShared(next.shared);
  };
  if (error && !read) return <p className="pc-flag err rr-error"><span className="material-symbols-rounded">error</span>{error}</p>;
  if (!read) return <LoadingSpinner />;
  const leftOut = [...(read.left_out || []), ...notLines.map(x => ({ id: x.key, order_number: x.label, why: x.why }))];
  return (
    <>
      {kind === 'invoice' ? (
        <p className="pc-muted">
          Each customer's product is a row, at the price it was invoiced at (ex VAT, per the item's unit). Make each a new contract for the period you choose — today's
          period to start with — on its own or with other rows' products of the same customer and category as one contract, or change one of the customer's contracts
          (Replace puts the new price on it). Nothing is written until you press Write. The invoices do not change and no line is linked — Add orders… on the contract
          counts an order by hand.
        </p>
      ) : (
        <p className="pc-muted">
          Each customer's product is a row, at the sale price shown on the screen (confirmed, else chosen, else proposed). Make each a new contract — on its own, or with
          other rows' products of the same customer and category as one contract — or add it to one of the customer's contracts. Nothing is written until you press Write;
          the lines are then linked to the contract they take. The prices on the orders do not change.
        </p>
      )}
      {!read.can_contract && <p className="pc-flag err"><span className="material-symbols-rounded">lock</span>{read.contract_block || "Needs 'Edit contract pricing'"}</p>}
      {error && <p className="pc-flag err rr-error"><span className="material-symbols-rounded">error</span>{error}</p>}
      {!rows.length && <p className="pc-flag warn"><span className="material-symbols-rounded">warning</span>None of these lines can be made a contract price — see why below.</p>}
      {rows.map((r, i) => {
        const offer = r.contract_offer;
        const orders = [...new Set(r.lines.map(l => l.order_number).filter(Boolean))];
        const priceOptions = (r.prices || []).map(p => ({
          value: String(p.value),
          label: `${money(p.value)} — ${p.line_ids.length} line${p.line_ids.length === 1 ? '' : 's'}${p.from.length ? ` (${p.from.join(' / ')})` : ''}`
        }));
        const one = (r.prices || [])[0];
        return (
          <div key={r.key} className={`cp-row ${problems[i] ? 'rr-attn' : ''}`}>
            <div className="cp-row-head">
              <span className="cp-row-no">Row {r.row}</span>
              <span className="name-with-badge">
                {r.accountNo && <span className="account-badge-inline">{r.accountNo}</span>}
                <span>{r.customer}</span>
              </span>
              <span className="cp-product">{r.itemCode}{r.product ? ` ${r.product}` : ''}</span>
              <span className="pc-muted">{r.lines.length} line{r.lines.length === 1 ? '' : 's'} · {orders.slice(0, 5).join(', ')}{orders.length > 5 ? ` +${orders.length - 5}` : ''} · {dayRange(r.lines)}</span>
            </div>
            <div className="cp-row-price">
              <span className="pc-muted">Contract price</span>
              {priceOptions.length > 1 ? (
                <div className="cp-price-pick">
                  <Dropdown escapeOverflow compact value={String(r.price)} options={priceOptions}
                    onChange={(v) => setPriceOf(prev => ({ ...prev, [i]: Number(v) }))} />
                  <span className="pc-flag warn cp-differs"><span className="material-symbols-rounded">warning</span>{kind === 'invoice'
                    ? 'It was invoiced at more than one price — the contract takes one; choose it.'
                    : 'Its lines differ — the contract takes one price; the lines at another keep theirs and are still linked to it.'}</span>
                </div>
              ) : (
                <span><strong>{money(r.price)}</strong>{one && one.from.length ? <span className="pc-muted"> — {one.from.join(' / ')}</span> : null}</span>
              )}
            </div>
            {r.unitNote && <p className="pc-flag warn"><span className="material-symbols-rounded">straighten</span>{r.unitNote}</p>}
            {offer && (
              <ContractFromPricesPanel row={r} index={i} rows={rows} offer={offer} state={offers[i]} offers={offers} shared={shared}
                heading="Make a contract" idPrefix="cp-contract"
                request={r.rep_request ? `${repContractRequestText(r.rep_request, { action: false })}${r.rep_request.lines > 1 ? ` (${r.rep_request.lines} lines)` : ''}` : null}
                members={offers[i]?.shared ? (members.get(offers[i].shared) || [i]) : [i]}
                problem={problems[i] || null}
                onChange={(patch) => setOffer(i, patch)}
                onShared={(patch) => (offers[i]?.shared ? setSharedOf(offers[i].shared, patch) : setOffer(i, patch))}
                onJoin={(j) => join(i, j)} onLeave={(k) => join(k, null)} />
            )}
          </div>
        );
      })}
      {leftOut.length > 0 && (
        <div className="cp-left-out" aria-label="Left out">
          <h5><span className="material-symbols-rounded">block</span>Left out: {leftOut.length} line{leftOut.length === 1 ? '' : 's'}</h5>
          <table className="rr-line-table">
            <tbody>
              {leftOut.map(l => (
                <tr key={`${l.id}|${l.why}`}>
                  <td>{l.order_number || (l.missing ? `Line ${l.id}` : '—')}</td>
                  <td>{l.account_no ? <span className="account-badge-inline">{l.account_no}</span> : null} {l.customer || ''}</td>
                  <td>{[l.product_code, l.product].filter(Boolean).join(' · ')}</td>
                  <td className="rr-notdone-why">{l.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {summary.length > 0 && (
        <div className="rr-summary" aria-label="What will be written">
          <h5><span className="material-symbols-rounded">fact_check</span>What will be written</h5>
          <ul>{summary.map(t => <li key={t}>{t}</li>)}</ul>
          {Object.keys(problems).length > 0 && <p className="rr-attn-why">{Object.keys(problems).length} contract{Object.keys(problems).length === 1 ? ' needs' : 's need'} attention and {Object.keys(problems).length === 1 ? 'is' : 'are'} left out.</p>}
        </div>
      )}
    </>
  );
}

export default ContractPricesModal;
